import {
  type BalanceResponse,
  type BucketAmounts,
  type ChargedModelCall,
  type CreditHistoryResponse,
  type CreditJournal,
  type PlayerBucket,
  RAW_PAX_CREDITS_PER_DISPLAY_CREDIT,
} from "@pax/api-contract/accounting/v1alpha1";
import type { LiveSubscription } from "@pax/api-contract/payments/v1alpha1";
import {
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { type Session, sessionKey, useSession } from "@/features/auth/queries";
import { api, orpc, publicOrpc, reachable } from "@/lib/api";

/** Moderation credits only fund moderation calls, so the credits a player can spend leave them out. */
export function spendableRaw(buckets: BucketAmounts): bigint {
  const { moderation: _moderation, ...spendable } = buckets;
  return Object.values(spendable).reduce((sum, raw) => sum + BigInt(raw ?? "0"), 0n);
}

function displayCredits(raw: bigint): number {
  return Number(raw) / Number(RAW_PAX_CREDITS_PER_DISPLAY_CREDIT);
}

/** The credits a player can spend, from their balance. */
export const spendableCredits = (balance: BalanceResponse) =>
  displayCredits(spendableRaw(balance.buckets));

/**
 * A player's whole balance, daily top-up included. Stripe webhooks change it with no signal to the
 * page, so it reads again when the player returns to the tab.
 */
export const balanceQuery = (userId: string) =>
  queryOptions({
    queryKey: [...orpc.accounting.players.balances.get.key(), { userId }],
    queryFn: async () => {
      const reply = await reachable(
        api.accounting.players.balances.get({ params: { userId }, headers: {} }),
      );
      if (reply.status !== 200) throw new Error("A balance read without an ETag answered 304.");
      return reply.body;
    },
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

export const creditBalanceQuery = (userId: string) =>
  queryOptions({ ...balanceQuery(userId), select: spendableCredits });

/**
 * Whether the signed-in player's spendable credits are under an automatic top-up threshold, so
 * turning the rule on buys its pack right away.
 */
export function useUnderThreshold(thresholdRaw: string | null): boolean {
  const userId = useSession().data?.user.id;
  const { data: spendable } = useQuery({
    ...balanceQuery(userId ?? ""),
    enabled: userId !== undefined,
    select: balance => spendableRaw(balance.buckets),
  });
  return thresholdRaw !== null && spendable !== undefined && spendable < BigInt(thresholdRaw);
}

/** A player's holds still open, and what each reserves. They finish in seconds, so it reads fresh. */
export const openHoldsQuery = (userId: string) =>
  orpc.accounting.players.openHolds.list.queryOptions({
    input: { params: { userId } },
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

/** The signed-in player's spendable credits already loaded, or null before the first balance read. */
export function cachedCredits(queryClient: QueryClient) {
  const userId = queryClient.getQueryData<Session>(sessionKey)?.user.id;
  const balance = userId && queryClient.getQueryData(balanceQuery(userId).queryKey);
  return balance ? spendableCredits(balance) : null;
}

export type CreditHistoryLine = {
  entryId: string;
  recordedAt: string;
  journal: CreditJournal;
  presetId?: string;
  credits: number;
  /** What each spendable balance gained or lost, leaving out balances that did not move. */
  buckets: { bucket: PlayerBucket; credits: number }[];
  modelCall: ChargedModelCall | null;
};

/**
 * Every loaded page, newest change first. A refetch racing a next-page load can repeat an entry
 * across two pages, so each entry is kept once.
 */
function creditHistoryLines(pages: CreditHistoryResponse[]): CreditHistoryLine[] {
  const seen = new Set<string>();
  const lines: CreditHistoryLine[] = [];
  for (const change of pages.flatMap(page => page.changes)) {
    if (seen.has(change.entryId)) continue;
    seen.add(change.entryId);
    const raw = spendableRaw(change.buckets);
    if (raw === 0n) continue;
    lines.push({
      entryId: change.entryId,
      recordedAt: change.recordedAt,
      journal: change.journal,
      presetId: change.presetId,
      credits: displayCredits(raw),
      buckets: Object.entries(change.buckets).flatMap(([bucket, amount]) =>
        bucket === "moderation" || !amount || BigInt(amount) === 0n
          ? []
          : [{ bucket: bucket as PlayerBucket, credits: displayCredits(BigInt(amount)) }],
      ),
      modelCall: change.modelCall,
    });
  }
  return lines;
}

export const creditHistoryQuery = (userId: string) =>
  infiniteQueryOptions({
    queryKey: [...orpc.accounting.players.history.list.key(), { userId }],
    queryFn: ({ pageParam }) =>
      reachable(
        api.accounting.players.history.list({
          params: { userId },
          query: pageParam ? { before: pageParam } : {},
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: CreditHistoryResponse) => page.nextBefore ?? undefined,
    select: data => creditHistoryLines(data.pages),
  });

/** The catalog changes only when prices do, and the API caches it for a minute. */
export const offersQuery = publicOrpc.payments.offers.list.queryOptions({
  select: response => response.offers,
  staleTime: 60_000,
});

/**
 * A kind of subscription as pax.co shows it: the Stripe one, which pax.co manages, else the one
 * billed through the App Store. A player who pays in both places has the App Store one as
 * `alsoInAppStore`.
 */
export type ShownSubscription = {
  current: LiveSubscription | null;
  alsoInAppStore: LiveSubscription | null;
};

const stripeFirst = (subscriptions: LiveSubscription[]): ShownSubscription => {
  const stripe = subscriptions.find(subscription => subscription.provider === "stripe");
  const appStore = subscriptions.find(subscription => subscription.provider === "app_store");
  return {
    current: stripe ?? appStore ?? null,
    alsoInAppStore: stripe && appStore ? appStore : null,
  };
};

/** The player's live plan and Patron subscription, each as pax.co shows it. */
export const mySubscriptionQuery = orpc.payments.subscription.get.queryOptions({
  select: ({ tokenSubscriptions, patronSubscriptions }) => ({
    plan: stripeFirst(tokenSubscriptions),
    patron: stripeFirst(patronSubscriptions),
  }),
});

export const topUpSettingsQuery = orpc.payments.topUpSettings.get.queryOptions();

export function refreshCredits(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: orpc.accounting.key() });
}

export function refreshTopUp(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: topUpSettingsQuery.queryKey });
}
